import React from 'react';
import { type NodeProps } from '@xyflow/react';
import { NodeCardShell } from '@aibuild/ui';
import type { ProcessNode as ProcessNodeRfType } from '../graphTypes';
import PortHandles from './PortHandles';
import { useProcessNodeViewModel } from './useProcessNodeViewModel';

/**
 * The single React Flow node renderer for every process node kind.
 *
 * <p>This component owns the node card chrome (header slot composition,
 * collapse, variant) and the runtime panels (artifacts), but delegates
 * ALL kind-specific decisions to the backend's
 * {@code NodeDefinitionDto}: the title + icon come from
 * {@code displayMetadata}, the body fields come from
 * {@code configFields}, the branched-output row comes from
 * {@code branches}, and the artifacts panel style comes from
 * {@code artifactsPanel}. Adding/renaming/restyling a kind is therefore
 * a pure backend change — there is no per-kind frontend file.</p>
 *
 * <p>Per-node interactive surface (matches the design's
 * `.ns-node__head` cluster):</p>
 * <ul>
 *   <li><b>Always-visible</b> {@code headerEnd}: status pill (only when
 *       the node has a non-idle run state) + Run icon-button (idle /
 *       running / done / warning).</li>
 *   <li><b>Hover-revealed</b> {@code actions}: 3-dot kebab opening the
 *       Duplicate / Delete ChipMenu.</li>
 *   <li><b>Selected-only</b> {@code floatingActions}: Duplicate + Delete
 *       icon cluster floating above the card. Visibility is controlled
 *       by the shell so this component just mounts the cluster.</li>
 * </ul>
 *
 * <p>For Tasks (the only kind whose definition id varies per instance —
 * each skill has its own definition class), the catalog lookup pivots
 * on the primary of the persisted {@code data.skillIds} so the editor renders the
 * right skill label / icon / instruction prompt without re-asserting
 * any of those values in the FE.</p>
 *
 * <p>Ports are rendered by {@link PortHandles} off
 * {@code data.ports} (the persisted port array seeded from
 * {@code PortSchemaDto}). Branched
 * outputs are rendered by BranchRow; when present, the default
 * port renderer's outputs are suppressed so the same port id isn't
 * wired by two competing handles.</p>
 */
const NODE_WIDTH_INPUT = 280;
const NODE_WIDTH_DEFAULT = 300;

const ProcessNode: React.FC<NodeProps<ProcessNodeRfType>> = (props) => {
  const vm = useProcessNodeViewModel(props);
  const width = vm.imageGenNodeWidth
    ?? (vm.kind === 'input' ? NODE_WIDTH_INPUT : NODE_WIDTH_DEFAULT);
  return (
    <NodeCardShell
      variant={vm.variant}
      state={vm.cardState}
      title={vm.titleNode}
      subtitle={vm.subtitle}
      icon={vm.iconNode}
      collapsed={vm.collapsed}
      onToggleCollapsed={vm.onToggleCollapsed}
      headerEnd={vm.headerEnd}
      actions={vm.actions}
      floatingActions={vm.floatingActions}
      dropTarget={vm.dropTarget}
      emphasis={vm.emphasis}
      published={vm.published}
      branches={vm.branchesSlot}
      width={width}
      dataTestId={vm.dataTestId}
    >
      {vm.bodyInner}
      {vm.outputRowSlot}
      {vm.runStatsSlot}
      <PortHandles {...vm.portHandlesProps} />
    </NodeCardShell>
  );
};

/**
 * Memoised so a drag tick only re-renders the moved node — relies on
 * the editor context being identity-stable on position-only changes
 * (see {@code ProcessEditorPage}).
 */
export default React.memo(ProcessNode);
